import { useEffect, useRef } from 'react';

/**
 * Zwei kleine Bausteine der Sitzungsbehandlung — Auftrag `A-sitzungssperre`.
 *
 * Sie stehen hier und nicht in `app/App.tsx`, weil beide **ohne React-Baum
 * prüfbar** sein sollen: der Rückkehrpfad als zwei reine Funktionen über
 * `sessionStorage`, die Leerlaufmessung als Hook mit einer hereingereichten
 * Frist. Dieselbe Bauform wie `notifyReauthRequired()` in `api/client.ts` und
 * aus demselben Grund.
 */

/**
 * Der Schlüssel des Rückkehrpfads.
 *
 * `sessionStorage` und nicht `localStorage`: Der Pfad gilt für **diesen** Tab
 * und diese eine unterbrochene Arbeit. In `localStorage` überlebte er das
 * Schliessen des Browsers und schickte den Anwender morgen früh auf die Seite,
 * an der er gestern hängengeblieben ist.
 */
const RUECKKEHR_SCHLUESSEL = 'fameacc:rueckkehr';

/**
 * Merkt sich, wo der Anwender war, als seine Sitzung ablief.
 *
 * **Wozu das nötig ist, obwohl die Anmeldemaske die Adresse gar nicht
 * anfasst.** `LoginPage` ersetzt in `App.tsx` die ganze Anwendung, ohne die
 * Adresszeile zu berühren — wer auf `/belege/<id>` abgemeldet wird, steht nach
 * dem Anmelden wieder dort. Das gilt aber nur, **solange die Seite nicht neu
 * geladen wird**: Ein Neuladen auf einer Adresse, die eine Sitzung
 * voraussetzt, ist der Regelfall nach einer Abmeldung («ich probier's mal mit
 * F5»), und danach hilft nur ein gemerkter Pfad.
 *
 * **Gemerkt wird Pfad, Abfrage und Fragment — nie die Herkunft.** Ein
 * gespeicherter vollständiger Verweis liesse sich von aussen setzen und wäre
 * damit eine offene Weiterleitung. Was hier steht, wird beim Zurückkehren
 * ausserdem nochmals geprüft (siehe `rueckkehrpfadHolen`).
 */
export function rueckkehrpfadMerken(ort: { pathname: string; search: string; hash: string }): void {
  try {
    const pfad = `${ort.pathname}${ort.search}${ort.hash}`;
    // Die Startseite zu merken bringt nichts und überschriebe einen
    // brauchbaren Pfad, wenn `/auth/me` beim Start eine 401 liefert.
    if (pfad === '/' || pfad === '') return;
    window.sessionStorage.setItem(RUECKKEHR_SCHLUESSEL, pfad);
  } catch {
    // Ein Browser mit gesperrtem Speicher verliert den Rückkehrpfad und sonst
    // nichts. Das ist eine Unbequemlichkeit und kein Fehlerfall.
  }
}

/**
 * Holt den gemerkten Pfad und vergisst ihn dabei.
 *
 * **Die Prüfung ist eng, und das ist der Punkt.** Angenommen wird nur, was mit
 * genau einem `/` beginnt. `//fremd.example` wäre ein schemaloser Verweis auf
 * eine fremde Herkunft und in `history.replaceState` genau die offene
 * Weiterleitung, gegen die es sonst keinen Schutz gäbe; `sessionStorage` ist
 * zwar nicht von aussen beschreibbar, aber eine XSS-Lücke irgendwo in der
 * Oberfläche wäre es, und eine Prüfung, die eine Zeile kostet, wird nicht
 * weggelassen.
 */
export function rueckkehrpfadHolen(): string | undefined {
  try {
    const pfad = window.sessionStorage.getItem(RUECKKEHR_SCHLUESSEL);
    window.sessionStorage.removeItem(RUECKKEHR_SCHLUESSEL);
    if (pfad === null || !pfad.startsWith('/') || pfad.startsWith('//')) return undefined;
    return pfad;
  } catch {
    return undefined;
  }
}

/**
 * Die Ereignisse, die als «da ist jemand» gelten.
 *
 * `pointerdown` statt `mousemove`: Eine Maus, die von einer Katze bewegt wird,
 * ist keine Aktivität, und ein Ereignis je Pixel wäre teuer. `visibilitychange`
 * steht bewusst NICHT hier — es meldet auch das Weggehen, und `App.tsx`
 * behandelt es getrennt, weil daraus zusätzlich eine Rückfrage beim Server
 * folgt.
 */
const AKTIVITAET = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'focus'] as const;

/**
 * Ruft `beiLeerlauf` auf, wenn die genannte Frist ohne Aktivität vergangen
 * ist.
 *
 * **Ein Zeitgeber, der regelmässig NACHSIEHT, und nicht einer, der bei jeder
 * Bewegung neu gestellt wird.** Ein `setTimeout`, das bei jedem Tastendruck
 * neu läuft, ist bei zügigem Tippen hundert Zeitgeber je Minute. Hier merkt
 * sich ein `ref` den letzten Zeitpunkt — das kostet eine Zuweisung —, und ein
 * einziges Intervall vergleicht ihn.
 *
 * **Der Takt ist gröber als die Frist, mit Absicht.** Er entscheidet, wie
 * lange nach dem Ablauf der Bildschirm noch offen daliegt; eine halbe Minute
 * auf sechzig ist ein Prozent. Wer feiner takten wollte, kaufte sich dafür
 * einen Weckruf je Sekunde in jedem offenen Tab.
 *
 * **Die Uhr dieses Hooks entscheidet nichts.** Sie zeigt den Sperrbildschirm
 * rechtzeitig; ob die Sitzung wirklich gesperrt ist, sagt ausschliesslich der
 * Server (`SESSION_LOCKED`, `GET /auth/me`). Läuft die Uhr des Browsers
 * daneben oder schläft der Rechner, ist die Folge ein Bildschirm, der ein paar
 * Sekunden zu früh oder zu spät erscheint — nie eine offene Sitzung, die
 * offen bleibt, obwohl sie es nicht sein dürfte.
 */
export function useLeerlauf(options: {
  readonly fristMs: number;
  readonly aktiv: boolean;
  readonly beiLeerlauf: () => void;
  readonly taktMs?: number;
}): void {
  const { fristMs, aktiv, beiLeerlauf } = options;
  const taktMs = options.taktMs ?? 30_000;
  const letzte = useRef(Date.now());
  // Der Rückruf steckt in einem `ref`, damit ein neu erzeugter Handler des
  // Aufrufers nicht bei jedem Rendern Lauscher ab- und wieder anmeldet.
  const rueckruf = useRef(beiLeerlauf);
  rueckruf.current = beiLeerlauf;

  useEffect(() => {
    if (!aktiv) return;

    letzte.current = Date.now();
    const merken = (): void => {
      letzte.current = Date.now();
    };
    for (const ereignis of AKTIVITAET) {
      window.addEventListener(ereignis, merken, { passive: true });
    }

    const takt = window.setInterval(() => {
      if (Date.now() - letzte.current >= fristMs) rueckruf.current();
    }, taktMs);

    return () => {
      for (const ereignis of AKTIVITAET) window.removeEventListener(ereignis, merken);
      window.clearInterval(takt);
    };
  }, [aktiv, fristMs, taktMs]);
}

/**
 * Die Leerlauffrist der Oberfläche: sechzig Minuten.
 *
 * **Sie steht doppelt — hier und als `IDLE_TTL_MS` in
 * `apps/api/src/auth/session.ts`.** Ein gemeinsamer Ort wäre
 * `@fameacc/contracts`, und das wäre falsch: Dieses Paket führt die Verträge
 * der Anfragen und Antworten, nicht die Betriebsparameter des Servers. Die
 * Folge einer Abweichung ist gutartig und benannt — ist die Zahl hier grösser,
 * antwortet die erste Anfrage danach mit `SESSION_LOCKED` und der Bildschirm
 * erscheint eben dann; ist sie kleiner, erscheint er zu früh und das erste
 * Entsperren ist auf einer noch offenen Sitzung wirkungslos, aber gültig.
 * Über die Sperre entscheidet in beiden Fällen der Server.
 */
export const LEERLAUF_MS = 60 * 60_000;
